import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  isMantraAdmin,
  isMantraProvider,
  isMcpRole,
  useCurrentProvider,
} from '../../../../../../Components/Permissions';
import { CareStatus, CareType, PaymentSource, StateCodes } from '../../../../../../graphQL';
import { useDrilldownContext } from '../../../../helpers';
import { EditPatientInfoProvider } from '../../types';
import { careStatusOptions, isStudentCareFlowEditable } from '../../utils';
import { ValidateGeoStateAndCareTypes } from '../../../../../../Hooks/useGeoStateAvailability';

type PatientCareFlowSectionProps = {
  careType: CareType;
  providers: EditPatientInfoProvider[];
  validateCareTypes?: ReturnType<ValidateGeoStateAndCareTypes>;
};

export const usePatientCareFlowSection = ({
  careType,
  providers,
  validateCareTypes,
}: PatientCareFlowSectionProps) => {
  const { currentProvider } = useCurrentProvider();
  const { user } = useDrilldownContext();
  const ctx = useFormContext();
  const careStatus: CareStatus = ctx.watch(`${careType}.careStatus`);
  const paymentSource: PaymentSource = ctx.watch(`${careType}.paymentSource`);

  const userState = user.primaryAddressState;
  const userOrgId = user.organization?.id;

  const showAllProviders =
    paymentSource !== PaymentSource.Organization ||
    Boolean(user.organization?.usesDedicatedGroupModel) ||
    Boolean(user.organization?.usesSessionsModel);

  const filteredProviders = useMemo(() => {
    const matchesCareType = (p: EditPatientInfoProvider) => p.careTypes.includes(careType);

    const matchesState = (p: EditPatientInfoProvider) =>
      !!userState && p.geoStates.includes(userState as StateCodes);

    const matchesOrg = (p: EditPatientInfoProvider) =>
      p.organizations.some(
        org => org.id === userOrgId || org.children.some(i => i.id === userOrgId)
      );

    return providers.filter(
      p =>
        isMantraProvider(p) &&
        matchesCareType(p) &&
        matchesState(p) &&
        (showAllProviders || matchesOrg(p))
    );
  }, [providers, userState, userOrgId, showAllProviders, careType]);

  const providerOptions = useMemo(
    () => filteredProviders.map(i => ({ id: i.id, label: i.name })),
    [filteredProviders]
  );

  const disabledCareStatus = !isStudentCareFlowEditable(currentProvider.role, careStatus);

  const filteredCareStatusOptions = useMemo(
    () =>
      careStatusOptions.filter(option => {
        if (!isStudentCareFlowEditable(currentProvider.role, careStatus)) {
          return option.id === careStatus;
        }

        if (
          isMcpRole(currentProvider.role) &&
          (careStatus === CareStatus.Cancelled || careStatus === CareStatus.OnHold)
        ) {
          return option.id === CareStatus.Active;
        }

        return (
          isMantraAdmin(currentProvider) || option.id === null || option.id === CareStatus.Active
        );
      }),
    [currentProvider, careStatus]
  );

  const careStatusSelectRules = useMemo(
    () => ({
      validate: {
        validateCareStatus: (value: CareStatus) => {
          if (value !== CareStatus.Active || !validateCareTypes) {
            return true;
          }

          const validationResponse = validateCareTypes([careType]);
          if (validationResponse.passed) {
            return true;
          }

          if (validationResponse.organizationFailed) {
            const { careTypes, stateName } = validationResponse.organizationFailed;
            return `Your organization is not contracted to refer students for ${careTypes} services in ${stateName}.`;
          }

          if (validationResponse.providerFailed) {
            const { careTypes, stateName } = validationResponse.providerFailed;
            return `Mantra Health is not contracted to refer students for ${careTypes} services in ${stateName}.`;
          }

          return true;
        },
      },
    }),
    [validateCareTypes, careType]
  );

  return {
    filteredCareStatusOptions,
    careStatusSelectRules,
    disabledCareStatus,
    providerOptions,
    careStatus,
    paymentSource,
  };
};
